'use client';

// module imports
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

// file imports
import { FeedbackQRCode, FormField, UpdateConfirmationModal } from '@/components';
import { generateEventSlug } from '@/lib/helpers';

const schema = z.object({
  title: z.string().min(3, { message: 'Title must be at least 3 characters' }),
  date: z.string().refine(
    (value) => {
      if (!value) return false;
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate >= today;
    },
    { message: 'Date cannot be in the past' }
  ),
  organization: z.string().min(2, { message: 'Organization name must be at least 2 characters' }).optional(),
  rate: z
    .string()
    .refine(
      (value) => {
        if (!value) return true; // Optional field
        if (!/^\d+$/.test(value)) return false;
        const numValue = Number(value);
        return !isNaN(numValue) && numValue >= 0;
      },
      { message: 'Rate must be a valid positive number' }
    )
    .optional(),
  presentationType: z.string().optional(),
  category: z.string().optional(),
});

type PresentationFormData = z.infer<typeof schema>;

interface PresentationFormProps {
  formData: PresentationFormData;
  onFormDataChange: (field: keyof PresentationFormData, value: string) => void;
  onSubmit: (data: Partial<PresentationFormData>) => void;
  isSubmitting: boolean;
  error: string;
  mode?: 'create' | 'edit';
}

export default function PresentationForm({ formData, onFormDataChange, onSubmit, isSubmitting, error, mode = 'create' }: PresentationFormProps) {
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<Partial<PresentationFormData> | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid, dirtyFields },
  } = useForm<PresentationFormData>({
    resolver: zodResolver(schema),
    defaultValues: formData,
    mode: 'onChange',
  });

  // Reset form when formData changes (for edit mode)
  useEffect(() => {
    if (mode === 'edit') {
      reset(formData);
    }
  }, [formData, reset, mode]);

  const watchedValues = watch();
  const previewSlug = watchedValues.title && watchedValues.date ? generateEventSlug(watchedValues.title, new Date(watchedValues.date)) : '';

  const handleFormSubmit = (data: PresentationFormData) => {
    if (mode === 'edit') {
      // Only send the fields that have been changed
      const changedFields: any = {};

      Object.keys(dirtyFields).forEach((key) => {
        const fieldKey = key as keyof PresentationFormData;
        if (dirtyFields[fieldKey]) {
          changedFields[fieldKey] = data[fieldKey];
        }
      });

      // Store the form data and show confirmation modal
      setPendingFormData(changedFields);
      setShowUpdateModal(true);
    } else {
      // Create mode - direct submission
      onFormDataChange('title', data.title);
      onFormDataChange('date', data.date);
      onFormDataChange('organization', data.organization || '');
      onFormDataChange('rate', data.rate || '');
      onFormDataChange('presentationType', data.presentationType || 'Presentation');
      onFormDataChange('category', data.category || 'Leadership');
      onSubmit(data);
    }
  };

  const handleUpdateConfirm = () => {
    if (pendingFormData) {
      // Update parent component with form data
      onFormDataChange('title', pendingFormData.title || '');
      onFormDataChange('date', pendingFormData.date || '');
      onFormDataChange('organization', pendingFormData.organization || '');
      onFormDataChange('rate', pendingFormData.rate || '');
      onFormDataChange('presentationType', pendingFormData.presentationType || 'Presentation');
      onFormDataChange('category', pendingFormData.category || 'Leadership');

      onSubmit(pendingFormData);
      setShowUpdateModal(false);
      setPendingFormData(null);
    }
  };

  const handleUpdateCancel = () => {
    setShowUpdateModal(false);
    setPendingFormData(null);
  };

  return (
    <div className="bg-white rounded-3xl p-8 shadow flex flex-col gap-8 w-full md:w-3/4 mx-auto mb-4">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <FormField id="title" label="Talk Title" type="text" placeholder="Enter talk title" {...register('title')} error={errors.title?.message} required />
          </div>
          <FormField id="date" label="Date" type="date" {...register('date')} error={errors.date?.message} required />
          <FormField id="organization" label="Organization" type="text" placeholder="Organization name" {...register('organization')} error={errors.organization?.message} />
          <FormField
            id="presentationType"
            label="Presentation Type"
            type="select"
            options={[
              { value: 'Presentation', label: 'Presentation' },
              { value: 'Keynote', label: 'Keynote' },
              { value: 'Education', label: 'Education' },
            ]}
            {...register('presentationType')}
            error={errors.presentationType?.message}
          />
          <FormField
            id="category"
            label="Category"
            type="select"
            options={[
              { value: 'Leadership', label: 'Leadership' },
              { value: 'Money', label: 'Money' },
              { value: 'AI', label: 'AI' },
            ]}
            {...register('category')}
            error={errors.category?.message}
          />
          <FormField id="rate" label="Rate (optional)" type="text" placeholder="e.g. 2500" {...register('rate')} error={errors.rate?.message} />
        </div>

        {previewSlug && <FeedbackQRCode eventSlug={previewSlug} title="Live QR Code" />}

        {error && <div className="text-red-600 text-center mb-4">{error}</div>}

        <div className="flex justify-end mt-8 gap-4">
          <button type="submit" className="bg-[#5167b2] text-white font-bold px-8 py-3 rounded-xl shadow hover:bg-[#4158a2] transition disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer" disabled={!isValid || isSubmitting || (mode === 'edit' && Object.keys(dirtyFields).length === 0)}>
            {isSubmitting ? 'Saving...' : mode === 'edit' ? 'Update Presentation' : 'Create Event'}
          </button>
        </div>
      </form>

      {/* Update Confirmation Modal - only shown in edit mode */}
      {mode === 'edit' && <UpdateConfirmationModal isOpen={showUpdateModal} onClose={handleUpdateCancel} onConfirm={handleUpdateConfirm} title="Update Presentation" message="Are you sure you want to update this presentation? The changes will be saved permanently." isUpdating={isSubmitting} />}
    </div>
  );
}
